import React, { useState } from 'react';
import { DebugPanel } from './shared/DebugPanel';
import { PromptEditor } from './shared/PromptEditor';

interface ArtifactHandle {
  data: string | null;
  loading: boolean;
  error: string | null;
  load: () => void;
}

interface StagePanelDetailsProps {
  stageId: string;
  title: string;
  runId: string | undefined;
  isLlmStage?: boolean;
  showPromptEditor?: boolean;
  isStageCompleted: boolean;
  prompt: ArtifactHandle;
  rawOutput: ArtifactHandle;
  jobStatus: string | null;
  jobError: any;
  jobProgress: any[];
}

export function StagePanelDetails({
  stageId,
  title,
  runId,
  isLlmStage,
  showPromptEditor,
  isStageCompleted,
  prompt,
  rawOutput,
  jobStatus,
  jobError,
  jobProgress,
}: StagePanelDetailsProps) {
  const [promptEditorOpen, setPromptEditorOpen] = useState(false);

  return (
    <details className="mt-6 border border-gray-300 rounded-lg bg-gray-50">
      <summary className="px-4 py-3 text-sm font-semibold text-gray-500 cursor-pointer hover:text-gray-700 select-none">
        Details
      </summary>
      <div className="px-4 py-3 border-t border-gray-200 bg-white rounded-b-lg space-y-2">
        {/* Prompt Editor — shown for LLM stages and stages with showPromptEditor */}
        {(isLlmStage || showPromptEditor) ? (
          <details
            className="border border-gray-200 rounded"
            onToggle={(e) => {
              if ((e.target as HTMLDetailsElement).open) setPromptEditorOpen(true);
            }}
          >
            <summary className="px-3 py-2 text-xs font-medium text-gray-600 cursor-pointer hover:bg-gray-50">
              Prompt Editor
            </summary>
            <div className="px-3 py-2 border-t border-gray-100">
              {promptEditorOpen ? <PromptEditor stageId={stageId} /> : <div className="text-xs text-gray-400">Expand to load...</div>}
            </div>
          </details>
        ) : null}

        {/* LLM artifacts — only for stages where the worker manages the LLM call */}
        {isLlmStage ? (
          <>
            {/* Last Prompt Sent to LLM */}
            {isStageCompleted ? (
              <details
                className="border border-gray-200 rounded"
                onToggle={(e) => {
                  if ((e.target as HTMLDetailsElement).open) prompt.load();
                }}
              >
                <summary className="px-3 py-2 text-xs font-medium text-gray-600 cursor-pointer hover:bg-gray-50">
                  Last Prompt Sent to LLM
                </summary>
                <div className="px-3 py-2 border-t border-gray-100">
                  {prompt.loading && <div className="text-xs text-gray-400">Loading prompt...</div>}
                  {prompt.error && <div className="text-xs text-red-500">{prompt.error}</div>}
                  {prompt.data && (
                    <pre className="text-xs text-gray-600 whitespace-pre-wrap bg-gray-50 border rounded p-2 max-h-96 overflow-y-auto">
                      {prompt.data}
                    </pre>
                  )}
                </div>
              </details>
            ) : (
              <div className="border border-gray-200 rounded px-3 py-2 text-xs text-gray-400">
                Last Prompt Sent to LLM — available after stage runs
              </div>
            )}

            {/* LLM Raw Output */}
            {isStageCompleted ? (
              <details
                className="border border-gray-200 rounded"
                onToggle={(e) => {
                  if ((e.target as HTMLDetailsElement).open) rawOutput.load();
                }}
              >
                <summary className="px-3 py-2 text-xs font-medium text-gray-600 cursor-pointer hover:bg-gray-50">
                  LLM Raw Output
                </summary>
                <div className="px-3 py-2 border-t border-gray-100">
                  {rawOutput.loading && <div className="text-xs text-gray-400">Loading output...</div>}
                  {rawOutput.error && <div className="text-xs text-red-500">{rawOutput.error}</div>}
                  {rawOutput.data && (
                    <pre className="text-xs text-gray-600 whitespace-pre-wrap bg-gray-50 border rounded p-2 max-h-96 overflow-y-auto">
                      {rawOutput.data}
                    </pre>
                  )}
                </div>
              </details>
            ) : (
              <div className="border border-gray-200 rounded px-3 py-2 text-xs text-gray-400">
                LLM Raw Output — available after stage runs
              </div>
            )}
          </>
        ) : !showPromptEditor ? (
          <div className="text-xs text-gray-400 italic py-1">No LLM calls in this stage.</div>
        ) : null}

        {/* Debug */}
        <DebugPanel
          title={`Debug: ${title}`}
          details={{
            stage_id: stageId,
            run_id: runId || null,
            job_status: jobStatus,
            job_error: jobError,
            job_progress: jobProgress,
          }}
        />
      </div>
    </details>
  );
}
